import React, {useEffect, useState} from "react";
import {BadgeOutlined} from "@mui/icons-material";
import EventBus from "../../util/EventBus";
import OwnerHierarchyDrawer from "./OwnerHierarchyDrawer";
import {Blue3, Grey6, Grey17} from "../../theme/styles";

/**
 * The Stewardship column: who owns this record, and where they sit.
 *
 * A POSITION NAME IS NOT SELF-EXPLANATORY. "Data and Technology Director" tells a reader which
 * position owns the record and nothing about where that sits -- whose department, above whom, under
 * which division. That is the question the name actually raises, and answering it used to mean
 * leaving for the administration app.
 *
 * ONE DRAWER, NOT ONE PER ROW. A cell renderer runs for every row in the grid, so a drawer mounted
 * inside the cell would be mounted a hundred times over to be opened once. The cell emits; a single
 * host, mounted per page, listens and owns the open state.
 */

type Payload = {name: string; usid: string};

/** The link a Stewardship cell renders. Grids call this from their cell renderer. */
export function StewardshipLink({name, usid}: {name: string | null; usid: string | null}) {
    if (!name) return <span style={{fontSize: 14, color: Grey6}}>&mdash;</span>;

    // WITHOUT A USID THERE IS NOTHING TO ROOT THE CHART AT, so the name renders as plain text rather
    // than as a link that opens an empty drawer. A link that cannot answer is worse than no link.
    if (!usid) {
        return (
            <div style={{display: "flex", alignItems: "center", gap: 6, minWidth: 0}}>
                <BadgeOutlined sx={{fontSize: 18, color: Grey6, flexShrink: 0}}/>
                <span style={cellText}>{name}</span>
            </div>
        );
    }

    return (
        <div style={{display: "flex", alignItems: "center", gap: 6, minWidth: 0}}>
            <BadgeOutlined sx={{fontSize: 18, color: Grey6, flexShrink: 0}}/>
            <span role="link"
                  tabIndex={0}
                  onClick={(event) => {
                      // The row opens the record; this opens the owner. Both on one cell, so the
                      // click must not travel.
                      event.stopPropagation();
                      EventBus.ownerHierarchy.emit("open", {name, usid} as Payload);
                  }}
                  onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.stopPropagation();
                      event.preventDefault();
                      EventBus.ownerHierarchy.emit("open", {name, usid} as Payload);
                  }}
                  title={name}
                  style={{...cellText, color: Blue3, cursor: "pointer"}}
                  onMouseOver={(e) => (e.currentTarget.style.textDecoration = "underline")}
                  onMouseOut={(e) => (e.currentTarget.style.textDecoration = "none")}>
                {name}
            </span>
        </div>
    );
}

const cellText: React.CSSProperties = {
    fontSize: 14, color: Grey17, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};

/**
 * Mounted once per page that shows a Stewardship column. Costs nothing until something opens it --
 * OwnerHierarchyDrawer only builds its chart while open.
 */
export function OwnerHierarchyHost() {
    const [owner, setOwner] = useState<Payload | null>(null);

    useEffect(() => {
        const open = (payload: Payload) => setOwner(payload);
        EventBus.ownerHierarchy.addListener("open", open);
        return () => { EventBus.ownerHierarchy.removeListener("open", open); };
    }, []);

    return (
        <OwnerHierarchyDrawer open={owner !== null}
                              onClose={() => setOwner(null)}
                              positionName={owner?.name ?? null}
                              positionUSID={owner?.usid ?? null}/>
    );
}
